import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Tag, Package, Play, Film } from "lucide-react";
import { fileUrl } from "../lib/api";
import { useI18n } from "../i18n";
import { RatingSummary } from "./Rating";
import { SponsoredBadge } from "./SponsoredBadge";
import { SaveButton } from "./SaveButton";
import { ShareButton } from "./ShareButton";

export const RequestCard = ({ req, index = 0, offerCount = 0 }) => {
  const navigate = useNavigate();
  const { t } = useI18n();

  // Prefer media list; fallback to legacy photo_path
  const media =
    req.media && req.media.length > 0
      ? req.media
      : req.photo_path
      ? [{ path: req.photo_path, type: "image" }]
      : [];
  const cover = media[0];
  const videoCount = media.filter((m) => m.type === "video").length;

  return (
    <article
      data-testid={`request-card-${req.id}`}
      onClick={() => navigate(`/r/${req.id}`)}
      style={{ animationDelay: `${index * 60}ms` }}
      className="fade-up card-hover group cursor-pointer relative overflow-hidden rounded-md border border-border bg-white flex flex-col"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {cover ? (
          cover.type === "image" ? (
            <img
              src={fileUrl(cover.path)}
              alt={req.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="relative h-full w-full">
              <video
                src={fileUrl(cover.path)}
                className="h-full w-full object-cover"
                muted
                playsInline
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="h-5 w-5 text-foreground ml-0.5" fill="currentColor" />
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-secondary">
            <Package className="h-12 w-12 text-muted-foreground/40" strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-sm px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase font-bold border border-border">
          <Tag className="h-3 w-3" strokeWidth={2.5} />
          {req.category}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          {videoCount > 0 && (
            <div className="rounded-full bg-white/95 backdrop-blur-sm px-2 py-1 text-[10px] font-bold border border-border flex items-center gap-1">
              <Film className="h-3 w-3" strokeWidth={2.5} />
              {videoCount}
            </div>
          )}
          {offerCount > 0 && (
            <div className="rounded-full bg-accent px-2.5 py-1 text-[10px] tracking-wider uppercase font-bold text-accent-foreground">
              {offerCount}
            </div>
          )}
          <SaveButton targetType="request" targetId={req.id} />
          <ShareButton
            targetType="request"
            targetId={req.id}
            title={req.title}
            description={req.description}
          />
        </div>
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors flex-1">
            {req.title}
          </h3>
        </div>
        {req.is_sponsored && <div><SponsoredBadge /></div>}
        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
          {req.description}
        </p>

        {req.rating_count > 0 && (
          <RatingSummary avg={req.rating_avg || 0} count={req.rating_count || 0} size={3} />
        )}

        <div className="grid grid-cols-2 gap-3 mt-auto pt-3 border-t border-border">
          <div>
            <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
              {t("quantity")}
            </div>
            <div className="font-mono-brand text-sm font-semibold mt-0.5">
              {req.quantity.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
              {t("budget")}
            </div>
            <div className="font-mono-brand text-sm font-semibold mt-0.5">{req.budget}</div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Package className="h-3.5 w-3.5" />
            <span className="font-mono-brand">{req.buyer_name}</span>
          </div>
          <div className="flex items-center gap-1 text-xs font-semibold text-primary group-hover:gap-2 transition-all">
            {t("view_details")}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>
    </article>
  );
};
